import React, {useState, useRef, useCallback, useEffect} from "react";
import {
  FileText,
  Image,
  Paperclip,
  FolderOpen,
  Eye,
  RotateCcw,
  Trash2,
} from "lucide-react";
import {useNotifications} from "../NotificationSystem";
import {
  uploadCharacterSheet,
  replaceCharacterSheet,
  deleteCharacterSheet,
  refreshCharacterSheetUrl,
} from "../../services/api/characterSheetService";
import {
  validateCharacterSheetFile,
  getFileExtension,
  getFileName,
} from "../../services/firebase/storage";

interface CharacterSheetUploadProps {
  characterId: string;
  // For display; may be ephemeral and refreshed elsewhere
  currentSheetUrl?: string;
  // Permanent storage path (new system)
  sheetPath?: string;
  // Original file name (new system)
  fileName?: string;
  // Notify parent with latest ephemeral URL for immediate viewing
  onSheetUploaded?: (sheetUrl: string) => void;
  onSheetDeleted?: () => void;
}

const CharacterSheetUpload: React.FC<CharacterSheetUploadProps> = ({
  characterId,
  currentSheetUrl,
  sheetPath,
  fileName,
  onSheetUploaded,
  onSheetDeleted,
}) => {
  const {addNotification} = useNotifications();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [displayUrl, setDisplayUrl] = useState<string | null>(currentSheetUrl || null);

  // Check if character has a sheet (new system: sheetPath/fileName, legacy: currentSheetUrl)
  const hasSheet = sheetPath || fileName || currentSheetUrl;

  // Fetch fresh signed URL for viewing/downloading
  const fetchSignedUrl = useCallback(async () => {
    if (!hasSheet || isLoadingUrl) return null;
    
    setIsLoadingUrl(true);
    try {
      const result = await refreshCharacterSheetUrl(characterId);
      setDisplayUrl(result.sheetUrl);
      return result.sheetUrl;
    } catch (error) {
      addNotification({
        type: "error",
        title: "Failed to Load Sheet",
        message: error instanceof Error ? error.message : "Failed to load character sheet",
      });
      return null;
    } finally {
      setIsLoadingUrl(false);
    }
  }, [characterId, hasSheet, isLoadingUrl, addNotification]);

  // Fetch signed URL when component mounts with a sheet
  useEffect(() => {
    if (hasSheet && !displayUrl && !isLoadingUrl) {
      fetchSignedUrl();
    }
  }, [hasSheet, displayUrl, isLoadingUrl, fetchSignedUrl]);

  // Handle file selection
  const handleFileSelect = useCallback(
    async (file: File) => {
      // Validate file
      const validation = validateCharacterSheetFile(file);
      if (!validation.isValid) {
        addNotification({
          type: "error",
          title: "Invalid File",
          message: validation.error || "File validation failed",
        });
        return;
      }

      setIsUploading(true);
      try {
        // Convert file to base64
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const base64Data = (e.target?.result as string).split(",")[1]; // Remove data URL prefix

            let result;
            if (currentSheetUrl) {
              // Replace existing sheet
              result = await replaceCharacterSheet({
                characterId,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                fileData: base64Data,
                oldSheetUrl: currentSheetUrl,
              });
            } else {
              // Upload new sheet
              result = await uploadCharacterSheet({
                characterId,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                fileData: base64Data,
              });
            }

            addNotification({
              type: "success",
              title: "Upload Successful",
              message: currentSheetUrl
                ? "Character sheet replaced successfully!"
                : "Character sheet uploaded successfully!",
            });

            onSheetUploaded?.(result.sheetUrl);
          } catch (error) {
            // Upload error - handled by notification system
            addNotification({
              type: "error",
              title: "Upload Failed",
              message:
                error instanceof Error
                  ? error.message
                  : "Failed to upload character sheet",
            });
          } finally {
            setIsUploading(false);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        // File reading error - handled by notification system
        addNotification({
          type: "error",
          title: "File Error",
          message: "Failed to read file",
        });
        setIsUploading(false);
      }
    },
    [characterId, currentSheetUrl, addNotification, onSheetUploaded]
  );

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  // Handle delete sheet
  const handleDeleteSheet = async () => {
    if (!hasSheet) return;

    setIsDeleting(true);
    try {
      await deleteCharacterSheet({
        characterId,
        // Pass currentSheetUrl if available, otherwise let backend use stored sheetPath
        sheetUrl: currentSheetUrl,
      });
      addNotification({
        type: "success",
        title: "Sheet Deleted",
        message: "Character sheet deleted successfully!",
      });
      setDisplayUrl(null);
      onSheetDeleted?.();
    } catch (error) {
      // Delete error - handled by notification system
      addNotification({
        type: "error",
        title: "Delete Failed",
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete character sheet",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle click to upload
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle view sheet button click
  const handleViewSheet = async () => {
    if (!hasSheet) return;
    
    // If we have a display URL, use it directly
    if (displayUrl) {
      window.open(displayUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Otherwise fetch a fresh URL
    const url = await fetchSignedUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Get file type icon
  const getFileTypeIcon = (url: string) => {
    const extension = getFileExtension(url);
    switch (extension) {
      case "pdf":
        return FileText;
      case "jpg":
      case "jpeg":
      case "png":
        return Image;
      default:
        return Paperclip;
    }
  };

  // Get file type label
  const getFileTypeLabel = (url: string) => {
    const extension = getFileExtension(url);
    switch (extension) {
      case "pdf":
        return "PDF Document";
      case "jpg":
      case "jpeg":
        return "JPEG Image";
      case "png":
        return "PNG Image";
      default:
        return "File";
    }
  };

  // Get display file name
  const getDisplayFileName = () => {
    if (fileName) return fileName;
    if (displayUrl) return getFileName(displayUrl);
    if (currentSheetUrl) return getFileName(currentSheetUrl);
    return "Character Sheet";
  };

  // Get display file type
  const getDisplayFileType = () => {
    if (fileName) {
      const extension = fileName.split('.').pop()?.toLowerCase();
      switch (extension) {
        case "pdf":
          return "PDF Document";
        case "jpg":
        case "jpeg":
          return "JPEG Image";
        case "png":
          return "PNG Image";
        default:
          return "File";
      }
    }
    if (displayUrl) return getFileTypeLabel(displayUrl);
    if (currentSheetUrl) return getFileTypeLabel(currentSheetUrl);
    return "File";
  };

  return (
    <div className="env-character-sheet">
      <div className="env-character-sheet-header">CHARACTER SHEET</div>

      {hasSheet ? (
        // Display existing sheet
        <div className="env-character-sheet-display">
          <div className="env-character-sheet-info">
            <div className="env-character-sheet-icon">
              {(() => {
                // Use displayUrl or currentSheetUrl for icon, fallback to fileName
                const urlForIcon = displayUrl || currentSheetUrl;
                if (urlForIcon) {
                  const IconComponent = getFileTypeIcon(urlForIcon);
                  return <IconComponent size={20} />;
                } else if (fileName) {
                  // Determine icon from file extension
                  const extension = fileName.split('.').pop()?.toLowerCase();
                  let IconComponent = Paperclip;
                  switch (extension) {
                    case "pdf":
                      IconComponent = FileText;
                      break;
                    case "jpg":
                    case "jpeg":
                    case "png":
                      IconComponent = Image;
                      break;
                  }
                  return <IconComponent size={20} />;
                }
                return <Paperclip size={20} />;
              })()}
            </div>
            <div className="env-character-sheet-details">
              <div className="env-character-sheet-name">
                {getDisplayFileName()}
              </div>
              <div className="env-character-sheet-type">
                {getDisplayFileType()}
              </div>
            </div>
          </div>
          <div className="env-character-sheet-actions">
            <button
              onClick={handleViewSheet}
              disabled={isLoadingUrl}
              className="btn btn-primary btn-small"
            >
              <Eye size={16} />
              {isLoadingUrl ? "Loading..." : "View Sheet"}
            </button>
            <button
              className="btn btn-secondary btn-small"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              <RotateCcw size={16} />
              Replace
            </button>
            <button
              className="btn btn-destructive btn-small delete-sheet-btn"
              onClick={handleDeleteSheet}
              disabled={isDeleting}
            >
              <Trash2 size={16} />
              <span className="delete-text">
                {isDeleting ? "Deleting..." : "Delete"}
              </span>
            </button>
          </div>
        </div>
      ) : (
        // Upload area
        <div
          className={`env-character-sheet-upload ${dragActive ? "env-character-sheet-upload-drag" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          <div className="env-character-sheet-icon">
            <FolderOpen size={32} />
          </div>
          {isUploading ? (
            <div className="env-character-sheet-uploading">
              Uploading character sheet...
            </div>
          ) : (
            <>
              Drag & Drop or{" "}
              <span className="env-character-sheet-link">Choose file</span> to
              upload
              <br />
              <span className="env-character-sheet-note">
                [PDF, JPEG, PNG - max 10MB]
              </span>
            </>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        id="character-sheet-file-input"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        aria-label="Upload character sheet"
        onChange={handleFileInputChange}
        style={{display: "none"}}
      />
    </div>
  );
};

export default CharacterSheetUpload;
